import { Avatar } from "@connected-repo/ui-mui/data-display/Avatar";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { navItems } from "@frontend/configs/nav.config";
import { useSessionInfo } from "@frontend/contexts/UserContext";
import { useWorkspace } from "@frontend/contexts/WorkspaceContext";
import { useStandsHome } from "@frontend/modules/exhibitor/hooks/useStandsHome";
import { useTenant } from "@frontend/tenant/TenantContext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link as RouterLink, useLocation } from "react-router";
import { NovuInbox } from "../notifications/NovuInbox";
import { BrandMark } from "./BrandMark";

/**
 * The one navbar, at every width. Brand, team, links, then the inbox and the
 * avatar on the right; below `sm` the links drop to their own row and
 * scroll sideways, so the page never loses its main nav to a bottom bar.
 * The avatar is a plain link to the profile page — theme, sign-out and the
 * team switch live there, not in a menu.
 */
export const TopNav = () => {
	const location = useLocation();
	const { activeWorkspace } = useWorkspace();
	const { isTenant } = useTenant();
	const { user } = useSessionInfo();
	const { show: standsHome, myEvents } = useStandsHome();

	const isActive = (path: string, matchPrefix?: boolean) =>
		location.pathname === path ||
		(matchPrefix === true && location.pathname.startsWith(`${path}/`));

	return (
		<Box
			component="header"
			sx={{
				position: "sticky",
				top: 0,
				zIndex: (t) => t.zIndex.appBar,
				bgcolor: "background.paper",
				borderBottom: "1px solid",
				borderColor: "divider",
			}}
		>
			<Box
				component="nav"
				aria-label="Main"
				sx={{
					maxWidth: 1200,
					mx: "auto",
					px: { xs: 2.5, sm: 4, md: 6 },
					py: { xs: 1.5, sm: 0 },
					minHeight: { sm: 68 },
					display: "flex",
					alignItems: "center",
					flexWrap: { xs: "wrap", sm: "nowrap" },
					gap: { xs: 1.5, sm: 3.5 },
				}}
			>
				<BrandMark />

				{/* The team pill opens the team's settings; switching teams lives on
				    /profile. A tenant host hides it: the brand mark IS the team, and
				    its settings are reached from the profile page instead. A team's
				    exhibitor has no settings to open. */}
				{!isTenant && activeWorkspace.id && activeWorkspace.role !== "Exhibitor" && (
					<Box
						component={RouterLink}
						to={`/teams/${activeWorkspace.id}/settings`}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							height: 36,
							px: 1.5,
							borderRadius: 9999,
							bgcolor: "surfaceEmphasis",
							color: "primary.main",
							fontSize: 13,
							fontWeight: 600,
							textDecoration: "none",
							whiteSpace: "nowrap",
							maxWidth: 200,
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{activeWorkspace.name}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						gap: 0.5,
						flex: "1 1 auto",
						order: { xs: 3, sm: 0 },
						flexBasis: { xs: "100%", sm: "auto" },
						overflowX: "auto",
						scrollbarWidth: "none",
						mx: { xs: -2.5, sm: 0 },
						px: { xs: 2.5, sm: 0 },
					}}
				>
					{navItems
						.filter((item) => item.exhibitor || activeWorkspace.role !== "Exhibitor")
						.map((item) => {
						const active = isActive(item.path, item.matchPrefix);
						return (
							<Box
								key={item.path}
								component={RouterLink}
								to={item.path}
								aria-current={active ? "page" : undefined}
								sx={{
									display: "inline-flex",
									alignItems: "center",
									height: 40,
									px: 1.75,
									borderRadius: "10px",
									fontSize: 14,
									fontWeight: active ? 600 : 500,
									color: active ? "heading" : "text.secondary",
									bgcolor: active ? "surfaceTint" : "transparent",
									textDecoration: "none",
									whiteSpace: "nowrap",
									transition: "color .2s ease, background .2s ease",
									"&:hover": { color: "heading" },
								}}
							>
								{item.label}
							</Box>
						);
					})}
					{/* On the stands home, "My events" — the person's own team's
					    events — sits at the end of the links row, out of the way of
					    the stands (root ADR-036). */}
					{standsHome && myEvents && isActive("/dashboard") && (
						<Box
							component="button"
							type="button"
							onClick={() => void myEvents()}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.75,
								height: 40,
								px: 1.75,
								ml: "auto",
								border: 0,
								borderRadius: "10px",
								bgcolor: "transparent",
								font: "inherit",
								fontSize: 14,
								fontWeight: 600,
								color: "primary.main",
								whiteSpace: "nowrap",
								cursor: "pointer",
								"&:hover": { color: "heading" },
							}}
						>
							My events
							<ArrowForwardIcon sx={{ fontSize: 18 }} />
						</Box>
					)}
				</Box>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
					<NovuInbox />
					<Box
						component={RouterLink}
						to="/profile"
						aria-label="Your profile"
						aria-current={isActive("/profile") ? "page" : undefined}
						sx={{
							display: "inline-flex",
							p: 0.5,
							borderRadius: "50%",
							transition: "transform .2s ease",
							"&:hover": { transform: "scale(1.05)" },
						}}
					>
						<Avatar
							src={user?.image || undefined}
							alt={user?.name || user?.email || "You"}
							sx={{
								width: 40,
								height: 40,
								border: "2px solid",
								borderColor: isActive("/profile") ? "primary.main" : "divider",
								transition: "border-color .2s ease",
							}}
						>
							{!user?.image && (user?.name?.[0] || user?.email?.[0] || "U")}
						</Avatar>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
